import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import CommentHighlight from './CommentHighlight.jsx';
import CommentPopover from './CommentPopover.jsx';
import { useTextSelection } from '../../hooks/useTextSelection.js';

/**
 * Enhanced content renderer with comment functionality
 */
export function CommentableContentRenderer({ 
  blocks = [], 
  comments = [], 
  onCommentCreate,
  onCommentClick,
  currentUserId,
  isCommentingEnabled = true
}) {
  const [activeComment, setActiveComment] = useState(null);
  const [highlightedCommentIds, setHighlightedCommentIds] = useState([]);
  const contentRef = useRef(null);

  // Text selection hook
  const {
    selection,
    selectionPosition,
    isSelecting,
    clearSelection,
    getSelectionData
  } = useTextSelection();

  /**
   * Handle comment creation from text selection
   */
  const handleCommentCreate = async (commentData) => {
    try {
      console.log('Creating comment with data:', commentData);
      
      // The commentData already contains all necessary fields from CommentPopover
      // (blockIndex, startOffset, endOffset, highlightedText, content)
      await onCommentCreate(commentData);
      clearSelection();
    } catch (error) {
      console.error('Error creating comment:', error);
    }
  };

  /**
   * Handle comment click
   */
  const handleCommentClick = (comment) => {
    setActiveComment(comment);
    
    // Find all comments for this specific text range
    const commentsForRange = comments.filter(c => 
      c.blockIndex === comment.blockIndex &&
      c.startOffset === comment.startOffset &&
      c.endOffset === comment.endOffset
    );
    
    // Set highlighted comment IDs
    setHighlightedCommentIds(commentsForRange.map(c => c.id));
    
    onCommentClick && onCommentClick(comment);
  };

  /**
   * Render a commentable text block
   */
  const renderCommentableText = (text, blockIndex, className = '') => {
    const blockComments = comments.filter(comment => comment.blockIndex === blockIndex);
    
    return (
      <CommentHighlight
        blockIndex={blockIndex}
        comments={blockComments}
        onCommentClick={handleCommentClick}
        className={className}
      >
        {text}
      </CommentHighlight>
    );
  };

  if (!Array.isArray(blocks)) {
    console.log("Nothing to see here");
    return null;
  }

  console.log("Rendering blocks with comments:", blocks.length, comments.length);
  if (!blocks || blocks.length === 0) {
    console.warn("No blocks to render.");
  }

  return (
    <div className="commentable-content" ref={contentRef}>
      {/* Comment popover */}
      {isCommentingEnabled && (
        <CommentPopover
          selection={selection}
          selectionPosition={selectionPosition}
          onSubmit={handleCommentCreate}
          onCancel={clearSelection}
          isVisible={isSelecting && selection}
        />
      )}

      <div className="space-y-6">
        {blocks.map((block, i) => {
          const blockIndex = i; // Use array index as block index
          
          switch (block.type) {
            case "paragraph":
              return (
                <p 
                  key={i} 
                  className="prose text-gray-900 dark:text-gray-100 mb-4"
                  data-block-index={blockIndex}
                >
                  {renderCommentableText(block.text, blockIndex)}
                </p>
              );
              
            case "heading":
              const HeadingTag = `h${block.level || 1}`;
              return (
                <HeadingTag 
                  key={i} 
                  className="font-bold text-gray-800 dark:text-gray-100"
                  data-block-index={blockIndex}
                >
                  {renderCommentableText(block.text, blockIndex)}
                </HeadingTag>
              );
              
            case "list":
              const ListTag = block.ordered ? 'ol' : 'ul';
              const listClass = block.ordered 
                ? "list-decimal list-inside space-y-1" 
                : "list-disc list-inside space-y-1";
              
              return (
                <ListTag 
                  key={i} 
                  className={listClass}
                  data-block-index={blockIndex}
                >
                  {(block.items || []).map((item, idx) => (
                    <li key={idx} className="text-gray-700 dark:text-gray-300">
                      {renderCommentableText(item, blockIndex)}
                    </li>
                  ))}
                </ListTag>
              );
              
            case "quote":
              return (
                <blockquote
                  key={i}
                  className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300"
                  data-block-index={blockIndex}
                >
                  {renderCommentableText(block.text, blockIndex)}
                  {block.cite && (
                    <cite className="block mt-2 not-italic text-gray-500 dark:text-gray-400">
                      — {renderCommentableText(block.cite, blockIndex)}
                    </cite>
                  )}
                </blockquote>
              );
              
            case "callout":
              const calloutVariants = {
                info: {
                  bgColor: 'bg-blue-50 dark:bg-blue-900/20',
                  borderColor: 'border-l-blue-500',
                  textColor: 'text-blue-900 dark:text-blue-100',
                  icon: '💡'
                },
                warning: {
                  bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
                  borderColor: 'border-l-yellow-500',
                  textColor: 'text-yellow-900 dark:text-yellow-100',
                  icon: '⚠️'
                },
                tip: {
                  bgColor: 'bg-green-50 dark:bg-green-900/20',
                  borderColor: 'border-l-green-500',
                  textColor: 'text-green-900 dark:text-green-100',
                  icon: '💡'
                },
                success: {
                  bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
                  borderColor: 'border-l-emerald-500',
                  textColor: 'text-emerald-900 dark:text-emerald-100',
                  icon: '✅'
                }
              };
              const variant = calloutVariants[block.variant] || calloutVariants.info;
              return (
                <div
                  key={i}
                  className={`${variant.bgColor} ${variant.borderColor} ${variant.textColor} border-l-4 p-4 rounded-r-lg mb-4`}
                  data-block-index={blockIndex}
                >
                  <div className="flex items-start">
                    <span className="mr-2 text-lg flex-shrink-0">{variant.icon}</span>
                    <div className="text-sm font-medium leading-relaxed">
                      {renderCommentableText(block.text, blockIndex)}
                    </div>
                  </div>
                </div>
              );
              
            case "code":
              return (
                <div key={i} className="mb-4" data-block-index={blockIndex}>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <code className={block.language ? `language-${block.language}` : ''}>
                      {block.code}
                    </code>
                  </pre>
                  {block.caption && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 text-center italic">
                      {renderCommentableText(block.caption, blockIndex)}
                    </p>
                  )}
                </div>
              );
              
            default:
              return (
                <div key={i} className="prose italic text-gray-400" data-block-index={blockIndex}>
                  Unsupported content type: {block.type}
                </div>
              );
          }
        })}
      </div>
    </div>
  );
}

CommentableContentRenderer.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string.isRequired,
    text: PropTypes.string,
    level: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.string),
    ordered: PropTypes.bool,
    cite: PropTypes.string,
    author: PropTypes.oneOf(['user', 'assistant']),
    context: PropTypes.string,
    variant: PropTypes.oneOf(['info', 'warning', 'tip', 'success']),
    language: PropTypes.string,
    code: PropTypes.string,
    caption: PropTypes.string,
  })),
  comments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    blockIndex: PropTypes.number.isRequired,
    startOffset: PropTypes.number.isRequired,
    endOffset: PropTypes.number.isRequired,
    highlightedText: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      email: PropTypes.string,
      profile_image: PropTypes.string
    }).isRequired,
    createdAt: PropTypes.string.isRequired,
    replies: PropTypes.array
  })),
  onCommentCreate: PropTypes.func.isRequired,
  onCommentClick: PropTypes.func,
  currentUserId: PropTypes.string,
  isCommentingEnabled: PropTypes.bool
};

export default CommentableContentRenderer;
